/**
 * Query key factory for consistent React Query key management
 * 
 * This module provides a centralized factory for generating consistent
 * query keys for all major entities in the doctor platform.
 * Follows the recommended hierarchical key structure for efficient invalidation.
 */

/**
 * Base query key factory interface
 */
interface QueryKeyFactory<T = any> {
  all: readonly string[];
  lists: () => readonly string[];
  list: (filters?: Record<string, any>) => readonly string[];
  details: () => readonly string[];
  detail: (id: string, ...params: any[]) => readonly string[];
}

/**
 * Authentication and user management keys
 */
export const authKeys = {
  all: ['auth'] as const,
  currentUser: () => [...authKeys.all, 'current-user'] as const,
  session: () => [...authKeys.all, 'session'] as const,
  profile: (userId: string) => [...authKeys.all, 'profile', userId] as const,
  permissions: (userId: string) => [...authKeys.all, 'permissions', userId] as const,
} as const;

/**
 * Doctor-related query keys
 */
export const doctorKeys = {
  all: ['doctors'] as const,
  lists: () => [...doctorKeys.all, 'list'] as const,
  list: (filters?: {
    status?: 'active' | 'inactive' | 'suspended';
    specialization?: string;
    verified?: boolean;
    search?: string;
  }) => [...doctorKeys.lists(), filters] as const,
  details: () => [...doctorKeys.all, 'detail'] as const,
  detail: (doctorId: string) => [...doctorKeys.details(), doctorId] as const,
  profile: (doctorId: string) => [...doctorKeys.detail(doctorId), 'profile'] as const,
  invitations: () => [...doctorKeys.all, 'invitations'] as const,
  invitation: (invitationId: string) => [...doctorKeys.invitations(), invitationId] as const,
  stats: (doctorId: string) => [...doctorKeys.detail(doctorId), 'stats'] as const,
} as const;

/**
 * Prescription-related query keys
 */
export const prescriptionKeys = {
  all: ['prescriptions'] as const,
  lists: () => [...prescriptionKeys.all, 'list'] as const,
  list: (filters?: {
    doctorId?: string;
    patientId?: string;
    pharmacyId?: string;
    status?: 'draft' | 'signed' | 'transmitted' | 'cancelled';
    dateRange?: { start: string; end: string };
    search?: string;
  }) => [...prescriptionKeys.lists(), filters] as const,
  details: () => [...prescriptionKeys.all, 'detail'] as const,
  detail: (prescriptionId: string) => [...prescriptionKeys.details(), prescriptionId] as const,
  items: (prescriptionId: string) => [...prescriptionKeys.detail(prescriptionId), 'items'] as const,
  history: (prescriptionId: string) => [...prescriptionKeys.detail(prescriptionId), 'history'] as const,
  // Doctor-specific prescriptions
  doctorPrescriptions: (doctorId: string) => [...prescriptionKeys.all, 'doctor', doctorId] as const,
  doctorPrescriptionsList: (doctorId: string, filters?: any) => 
    [...prescriptionKeys.doctorPrescriptions(doctorId), 'list', filters] as const,
  doctorPrescriptionDetail: (doctorId: string, prescriptionId: string) =>
    [...prescriptionKeys.doctorPrescriptions(doctorId), 'detail', prescriptionId] as const,
} as const;

/**
 * Patient-related query keys
 */
export const patientKeys = {
  all: ['patients'] as const,
  lists: () => [...patientKeys.all, 'list'] as const,
  list: (filters?: {
    search?: string;
    ageRange?: { min: number; max: number };
    gender?: string;
    hasInsurance?: boolean;
  }) => [...patientKeys.lists(), filters] as const,
  details: () => [...patientKeys.all, 'detail'] as const,
  detail: (patientId: string) => [...patientKeys.details(), patientId] as const,
  prescriptions: (patientId: string) => [...patientKeys.detail(patientId), 'prescriptions'] as const,
  history: (patientId: string) => [...patientKeys.detail(patientId), 'history'] as const,
  allergies: (patientId: string) => [...patientKeys.detail(patientId), 'allergies'] as const,
  medications: (patientId: string) => [...patientKeys.detail(patientId), 'medications'] as const,
} as const;

/**
 * Medicine and drug information keys
 */
export const medicineKeys = {
  all: ['medicines'] as const,
  lists: () => [...medicineKeys.all, 'list'] as const,
  list: (filters?: {
    category?: string;
    manufacturer?: string;
    controlled?: boolean;
    requiresPrescription?: boolean;
    search?: string;
  }) => [...medicineKeys.lists(), filters] as const,
  details: () => [...medicineKeys.all, 'detail'] as const,
  detail: (medicineId: string) => [...medicineKeys.details(), medicineId] as const,
  variants: (medicineId: string) => [...medicineKeys.detail(medicineId), 'variants'] as const,
  drugInfo: (medicineId: string) => [...medicineKeys.detail(medicineId), 'drug-info'] as const,
  interactions: (medicineIds: string[]) => [...medicineKeys.all, 'interactions', ...medicineIds.sort()] as const,
  search: (query: string) => [...medicineKeys.all, 'search', query] as const,
} as const;

/**
 * Pharmacy-related query keys
 */
export const pharmacyKeys = {
  all: ['pharmacies'] as const,
  lists: () => [...pharmacyKeys.all, 'list'] as const,
  list: (filters?: {
    status?: 'active' | 'inactive';
    region?: string;
    search?: string;
  }) => [...pharmacyKeys.lists(), filters] as const,
  details: () => [...pharmacyKeys.all, 'detail'] as const,
  detail: (pharmacyId: string) => [...pharmacyKeys.details(), pharmacyId] as const,
  inventory: (pharmacyId: string) => [...pharmacyKeys.detail(pharmacyId), 'inventory'] as const,
  staff: (pharmacyId: string) => [...pharmacyKeys.detail(pharmacyId), 'staff'] as const,
} as const;

/**
 * Notification and messaging keys
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (filters?: {
    userId?: string;
    read?: boolean;
    type?: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
  }) => [...notificationKeys.lists(), filters] as const,
  details: () => [...notificationKeys.all, 'detail'] as const,
  detail: (notificationId: string) => [...notificationKeys.details(), notificationId] as const,
  unreadCount: (userId: string) => [...notificationKeys.all, 'unread-count', userId] as const,
} as const;

/**
 * System and configuration keys
 */
export const systemKeys = {
  all: ['system'] as const,
  config: () => [...systemKeys.all, 'config'] as const,
  settings: (userId: string) => [...systemKeys.all, 'settings', userId] as const,
  permissions: () => [...systemKeys.all, 'permissions'] as const,
  healthCheck: () => [...systemKeys.all, 'health-check'] as const,
  exchangeRates: () => [...systemKeys.all, 'exchange-rates'] as const,
} as const;

/**
 * Audit and compliance keys
 */
export const auditKeys = {
  all: ['audit'] as const,
  logs: (filters?: {
    userId?: string;
    table?: string;
    action?: string;
    dateRange?: { start: string; end: string };
  }) => [...auditKeys.all, 'logs', filters] as const,
  signatures: (documentId: string) => [...auditKeys.all, 'signatures', documentId] as const,
  complianceReports: (type: string, period: string) => 
    [...auditKeys.all, 'compliance', type, period] as const,
} as const;

/**
 * Analytics and reporting keys
 */
export const analyticsKeys = {
  all: ['analytics'] as const,
  dashboard: (doctorId: string, period: string) => 
    [...analyticsKeys.all, 'dashboard', doctorId, period] as const,
  prescriptionMetrics: (doctorId: string, period: string) =>
    [...analyticsKeys.all, 'prescriptions', doctorId, period] as const,
  patientMetrics: (doctorId: string, period: string) =>
    [...analyticsKeys.all, 'patients', doctorId, period] as const,
  drugUtilization: (doctorId: string, period: string) =>
    [...analyticsKeys.all, 'drug-utilization', doctorId, period] as const,
} as const;

/**
 * Query key utilities
 */
export const queryKeyUtils = {
  /**
   * Create a custom query key with proper typing
   */
  createKey: <T extends readonly any[]>(...segments: T): T => segments,

  /**
   * Check if a key matches a pattern
   */
  matchesPattern: (key: readonly any[], pattern: readonly any[]): boolean => {
    if (pattern.length > key.length) return false;
    return pattern.every((segment, index) => key[index] === segment);
  },

  /**
   * Get all keys that match a pattern
   */
  getMatchingKeys: (pattern: readonly any[]): readonly any[][] => {
    // This would need to be implemented with access to the query cache
    // For now, return empty array as placeholder
    return [];
  },

  /**
   * Serialize key for debugging
   */
  serialize: (key: readonly any[]): string => {
    return JSON.stringify(key);
  },

  /**
   * Create invalidation helper for a key factory
   */
  createInvalidator: <T extends Record<string, any>>(keyFactory: T) => {
    return {
      all: () => keyFactory.all,
      lists: () => keyFactory.lists?.() || keyFactory.all,
      details: () => keyFactory.details?.() || keyFactory.all,
      specific: (key: any) => key,
    };
  },
};

/**
 * Invalidation helpers for common operations
 */
export const invalidationHelpers = {
  /**
   * Invalidate all prescription-related data for a doctor
   */
  invalidateDoctorPrescriptions: (doctorId: string) => [
    prescriptionKeys.doctorPrescriptions(doctorId),
    doctorKeys.stats(doctorId),
    analyticsKeys.prescriptionMetrics(doctorId, 'all'),
  ],

  /**
   * Invalidate patient-related data
   */
  invalidatePatientData: (patientId: string) => [
    patientKeys.detail(patientId),
    patientKeys.prescriptions(patientId),
    patientKeys.history(patientId),
  ],

  /**
   * Invalidate medicine data
   */
  invalidateMedicineData: (medicineId: string) => [
    medicineKeys.detail(medicineId),
    medicineKeys.variants(medicineId),
    medicineKeys.drugInfo(medicineId),
  ],

  /**
   * Invalidate user session data
   */
  invalidateUserSession: (userId: string) => [
    authKeys.currentUser(),
    authKeys.session(),
    authKeys.profile(userId),
    doctorKeys.detail(userId),
  ],

  /**
   * Invalidate system-wide data (after major updates)
   */
  invalidateSystemData: () => [
    systemKeys.config(),
    systemKeys.permissions(),
    pharmacyKeys.lists(),
    medicineKeys.lists(),
  ],
};

/**
 * Export all key factories as a single object for convenience
 */
export const queryKeys = {
  auth: authKeys,
  doctors: doctorKeys,
  prescriptions: prescriptionKeys,
  patients: patientKeys,
  medicines: medicineKeys,
  pharmacies: pharmacyKeys,
  notifications: notificationKeys,
  system: systemKeys,
  audit: auditKeys,
  analytics: analyticsKeys,
} as const;

export default queryKeys;